import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { projectsApi } from "../../api/projects";
import ProjectDeploymentCard from "../../components/dashboard/ProjectDeploymentCard";
import ProjectDeploymentHistory from "../../components/dashboard/ProjectDeploymentHistory";
import ProjectAccessManager from "../../components/dashboard/ProjectAccessManager";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import {
  PROJECT_MODULES,
  applyModuleChange,
  normalizeProjectModules,
  normalizeProjectPlanCode,
  serializeModulesForApi,
} from "../../utils/projectModules";
import { isSuperAdmin } from "../../utils/roles";
import { hasProjectPermission, normalizeProjectAccess } from "../../utils/projectAccess";
import {
  PROJECT_BUSINESS_GOALS,
  PROJECT_BUSINESS_PROFILES,
  PROJECT_CAPABILITIES,
  applyBusinessProfileSuggestions,
  businessGoalsForProfile,
  normalizeProjectBusinessProfile,
  normalizeProjectBusinessGoals,
  normalizeProjectCapabilities,
  projectBusinessGoals,
  projectBusinessProfileDefinition,
} from "../../utils/projectCapabilities";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

const MODULE_ICONS = {
  landing: <PublicRoundedIcon />,
  products: <Inventory2RoundedIcon />,
  orders: <ListAltRoundedIcon />,
  ecommerce: <PaymentsRoundedIcon />,
  system: <WarehouseRoundedIcon />,
  stats: <AnalyticsRoundedIcon />,
  webpush: <NotificationsActiveRoundedIcon />,
};

function memberId(member) {
  return member.id || member._id;
}

function memberUser(member) {
  return member.userId || member.user || {};
}

function moduleActionPath(projectId, key) {
  if (key === "landing") return `/dashboard/proyectos/${projectId}/pagina`;
  if (key === "products") return `/dashboard/proyectos/${projectId}/productos`;
  if (key === "orders") return `/dashboard/proyectos/${projectId}/pedidos`;
  if (key === "ecommerce") return `/dashboard/proyectos/${projectId}/configuracion/ecommerce`;
  if (key === "system") return `/dashboard/proyectos/${projectId}/sistema`;
  if (key === "stats") return `/dashboard/proyectos/${projectId}/estadisticas`;
  if (key === "webpush") return `/dashboard/proyectos/${projectId}/configuracion/notificaciones`;
  return "";
}

export default function ProjectDetailPage({ embedded = false, section = "all" }) {
  const isMobileViewport = useMediaQuery("(max-width:899.95px)", { noSsr: true });
  const compactMobile = embedded && isMobileViewport;
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loadProjects, setActiveProject } = useProjects();
  const canEditTechnical = isSuperAdmin(user);

  const [project, setProject] = React.useState(null);
  const [member, setMember] = React.useState(null);
  const [memberAccess, setMemberAccess] = React.useState(normalizeProjectAccess());
  const [members, setMembers] = React.useState([]);
  const [form, setForm] = React.useState({
    name: "",
    plan: "catalogo",
    status: "active",
    domains: "",
    modules: normalizeProjectModules({}),
    businessProfile: "",
    businessGoals: [],
    capabilities: normalizeProjectCapabilities({}),
  });
  const [memberForm, setMemberForm] = React.useState({ email: "", role: "viewer" });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  const canManageMercadoPago = canEditTechnical || ["owner", "admin"].includes(member?.role);
  const canManageProjectAccess = canEditTechnical || hasProjectPermission(memberAccess, "project.members.manage");
  const canManageBusinessConfiguration = canEditTechnical || hasProjectPermission(memberAccess, "project.settings.manage");

  React.useEffect(() => {
    if (loading || section !== "general") return;
    const area = new URLSearchParams(location.search).get("area");
    if (!area) return;
    const target = document.getElementById(`project-settings-${area}`);
    if (!target) return;
    const frame = window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [loading, location.search, section]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await projectsApi.get(projectId);
      const nextProject = data.project;
      const resolvedAccess = normalizeProjectAccess(data.memberAccess || data.member || {});
      setProject(nextProject);
      setMember(data.member || null);
      setMemberAccess(resolvedAccess);
      if (nextProject) setActiveProject(nextProject);
      setForm({
        name: nextProject.name || "",
        plan: normalizeProjectPlanCode(nextProject.billing?.planCode || nextProject.plan || "catalogo"),
        status: nextProject.status || "active",
        domains: (nextProject.domains || []).join("\n"),
        modules: normalizeProjectModules(nextProject.modules),
        businessProfile: normalizeProjectBusinessProfile(nextProject.businessProfile),
        businessGoals: projectBusinessGoals(nextProject),
        capabilities: normalizeProjectCapabilities(nextProject.capabilities),
      });

      if (canEditTechnical || hasProjectPermission(resolvedAccess, "project.members.manage")) {
        try {
          const membersData = await projectsApi.listMembers(projectId);
          setMembers(membersData.members || []);
        } catch (_membersError) {
          setMembers([]);
        }
      } else {
        setMembers([]);
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar el proyecto");
    } finally {
      setLoading(false);
    }
  }, [canEditTechnical, projectId, setActiveProject]);

  React.useEffect(() => {
    load();
  }, [load]);

  const change = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const changeModule = (key, checked) => {
    setForm((previous) => ({
      ...previous,
      modules: applyModuleChange(previous.modules, key, checked),
    }));
  };

  const changeBusinessProfile = (value) => {
    const businessProfile = normalizeProjectBusinessProfile(value);
    setForm((previous) => ({
      ...previous,
      businessProfile,
    }));
  };

  const toggleBusinessGoal = (goalKey) => {
    setForm((previous) => {
      const selected = new Set(normalizeProjectBusinessGoals(previous.businessGoals));
      if (selected.has(goalKey)) selected.delete(goalKey);
      else selected.add(goalKey);
      return {
        ...previous,
        businessGoals: Array.from(selected),
      };
    });
  };

  const applySelectedProfileSuggestions = () => {
    if (!form.businessProfile) return;
    setForm((previous) => ({
      ...previous,
      capabilities: applyBusinessProfileSuggestions(
        previous.capabilities,
        previous.businessProfile,
      ),
    }));
  };

  const changeCapability = (key, checked) => {
    setForm((previous) => ({
      ...previous,
      capabilities: {
        ...normalizeProjectCapabilities(previous.capabilities),
        [key]: checked === true,
      },
    }));
  };

  const handleDeploymentProjectChange = React.useCallback(
    (nextProject) => {
      if (!nextProject) return;
      setProject(nextProject);
      setActiveProject(nextProject);
      setForm((previous) => ({
        ...previous,
        domains: (nextProject.domains || []).join("\n"),
      }));
    },
    [setActiveProject]
  );

  const save = async (event) => {
    event?.preventDefault?.();
    if (!canEditTechnical) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = {
        name: form.name,
        plan: form.plan,
        status: form.status,
        domains: form.domains,
        modules: serializeModulesForApi(form.modules),
      };
      const data = await projectsApi.update(projectId, payload);
      setProject(data.project);
      setActiveProject(data.project);
      setMessage(section === "modules" ? "Módulos actualizados." : "Configuración general actualizada.");
      await loadProjects();
      await load();
    } catch (err) {
      setError(err.message || "No se pudo guardar el proyecto");
    } finally {
      setSaving(false);
    }
  };

  const saveBusinessConfiguration = async () => {
    if (!canManageBusinessConfiguration) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const data = await projectsApi.updateBusinessConfiguration(projectId, {
        businessProfile: form.businessProfile,
        businessGoals: normalizeProjectBusinessGoals(form.businessGoals),
        capabilities: normalizeProjectCapabilities(form.capabilities),
      });
      const nextProject = data.project || project;
      setProject(nextProject);
      setActiveProject(nextProject);
      setForm((previous) => ({
        ...previous,
        businessProfile: normalizeProjectBusinessProfile(nextProject?.businessProfile),
        businessGoals: projectBusinessGoals(nextProject),
        capabilities: normalizeProjectCapabilities(nextProject?.capabilities),
      }));
      setMessage("Perfil, objetivos y capacidades actualizados.");
      await loadProjects();
    } catch (err) {
      setError(err.message || "No se pudo guardar el perfil del negocio");
    } finally {
      setSaving(false);
    }
  };

  const addMember = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await projectsApi.addMember(projectId, memberForm);
      setMemberForm({ email: "", role: "viewer" });
      setMessage("Colaborador agregado.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo agregar el colaborador");
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (memberToRemove) => {
    const userInfo = memberUser(memberToRemove);
    const ok = window.confirm(`¿Quitar acceso a ${userInfo.email || "este usuario"}?`);
    if (!ok) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await projectsApi.removeMember(projectId, memberId(memberToRemove));
      setMessage("Colaborador eliminado.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo eliminar el colaborador");
    } finally {
      setSaving(false);
    }
  };

  const activeModules = PROJECT_MODULES.filter((module) => form.modules[module.key]);
  const selectedBusinessProfile = projectBusinessProfileDefinition(form.businessProfile);
  const selectedBusinessGoalKeys = new Set(normalizeProjectBusinessGoals(form.businessGoals));
  const suggestedBusinessGoals = businessGoalsForProfile(form.businessProfile);
  const suggestedBusinessGoalKeys = new Set(suggestedBusinessGoals.map((goal) => goal.key));
  const visibleBusinessGoals = [
    ...suggestedBusinessGoals,
    ...PROJECT_BUSINESS_GOALS.filter(
      (goal) => !suggestedBusinessGoalKeys.has(goal.key),
    ),
  ];
  const explicitBusinessGoalsOutsideProfile = visibleBusinessGoals.filter(
    (goal) => selectedBusinessGoalKeys.has(goal.key) && !suggestedBusinessGoalKeys.has(goal.key),
  );
  const selectedSuggestedCapabilities = new Set(selectedBusinessProfile?.suggestedCapabilities || []);
  const showModules = section === "all" || section === "modules";
  const showGeneral = section === "all" || section === "general";
  const showAccess = section === "all" || section === "access";
  const subscriptionPath = `/dashboard/proyectos/${projectId}/configuracion/suscripcion`;

  return (
    <Stack spacing={3}>
      {!embedded && (
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
              {project?.name || "Proyecto"}
            </Typography>
            <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
              {project ? `/${project.slug} · DB: ${project.dbName}` : "Cargando configuración..."}
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              startIcon={<WorkspacePremiumRoundedIcon />}
              onClick={() => navigate(subscriptionPath)}
              variant="outlined"
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: BRAND.orange }}
            >
              {canEditTechnical ? "Plan del cliente" : "Mejorar plan"}
            </Button>
            {form.modules.landing && (
              <Button
                startIcon={<PublicRoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/pagina`)}
                variant="outlined"
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
              >
                Landing
              </Button>
            )}
            {form.modules.products && (
              <Button
                startIcon={<Inventory2RoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/productos`)}
                variant="outlined"
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
              >
                Productos
              </Button>
            )}
            {form.modules.orders && (
              <Button
                startIcon={<ListAltRoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/pedidos`)}
                variant="contained"
                sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
              >
                Pedidos
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      {loading && <Alert severity="info">Cargando proyecto...</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      {showModules && (
      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 2.5 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                Niveles y módulos del proyecto
              </Typography>
              <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.5 }}>
                Landing → Productos → Pedidos → E-commerce. Sistema y Estadísticas se habilitan según la operación contratada.
              </Typography>
            </Box>
            <Chip
              icon={<SettingsSuggestRoundedIcon />}
              label={`${activeModules.length} de ${PROJECT_MODULES.length} activos`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 900, alignSelf: "flex-start" }}
            />
          </Stack>

          {!canEditTechnical && (
            <Alert
              severity="info"
              sx={{ mb: 2.5 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate(subscriptionPath)}
                  sx={{ textTransform: "none", fontWeight: 900 }}
                >
                  Ver servicios
                </Button>
              }
            >
              Tu suscripción actual habilita estas secciones. Para activar otro módulo, administrá tus servicios mensuales.
            </Alert>
          )}

          <Grid container spacing={2}>
            {PROJECT_MODULES.map((module) => {
              const enabled = Boolean(form.modules[module.key]);
              const canOpen = enabled && (module.key !== "ecommerce" || canManageMercadoPago);
              return (
                <Grid item xs={12} md={6} xl={module.key === "stats" ? 12 : 6} key={module.key}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 3.5,
                      border: `1px solid ${enabled ? "rgba(243,90,58,0.35)" : "rgba(25,36,47,0.08)"}`,
                      bgcolor: enabled ? "rgba(243,90,58,0.035)" : "#FAFAFC",
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Box sx={{ width: 46, height: 46, flexShrink: 0, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: enabled ? "rgba(243,90,58,0.12)" : "rgba(25,36,47,0.06)", color: enabled ? BRAND.orange : "rgba(25,36,47,0.5)" }}>
                            {MODULE_ICONS[module.key]}
                          </Box>
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Chip size="small" label={`Nivel ${module.level}`} sx={{ fontWeight: 900 }} />
                              <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
                                {module.label}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.64)", mt: 0.75 }}>
                              {module.description}
                            </Typography>
                          </Box>
                        </Stack>
                        <Switch
                          disabled={!canEditTechnical}
                          checked={enabled}
                          onChange={(event) => changeModule(module.key, event.target.checked)}
                          inputProps={{ "aria-label": `Habilitar ${module.label}` }}
                        />
                      </Stack>

                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                        {module.includes.map((item) => (
                          <Chip key={item} size="small" label={item} variant="outlined" />
                        ))}
                      </Stack>

                      {canOpen && (
                        <Button
                          onClick={() => navigate(moduleActionPath(projectId, module.key))}
                          sx={{ mt: 1.5, p: 0, minWidth: 0, color: BRAND.orange, textTransform: "none", fontWeight: 900 }}
                        >
                          {module.key === "ecommerce" ? "Configurar Mercado Pago" : module.key === "webpush" ? "Administrar notificaciones" : `Abrir ${module.label}`}
                        </Button>
                      )}
                      {!enabled && !canEditTechnical && (
                        <Button
                          startIcon={<WorkspacePremiumRoundedIcon />}
                          onClick={() => navigate(`${subscriptionPath}?feature=${encodeURIComponent(module.label)}`)}
                          sx={{ mt: 1.5, p: 0, minWidth: 0, color: BRAND.orange, textTransform: "none", fontWeight: 900 }}
                        >
                          Contratar servicio para activar
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {canEditTechnical && (
            <Button
              type="button"
              disabled={saving}
              startIcon={<SaveRoundedIcon />}
              onClick={save}
              variant="contained"
              sx={{ mt: 2.5, bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
            >
              {saving ? "Guardando..." : "Guardar funcionalidades"}
            </Button>
          )}
        </CardContent>
      </Card>
      )}

      {(showGeneral || showAccess) && (
      <Grid container spacing={3}>
        {showGeneral && (
        <Grid item xs={12} lg={showAccess ? 7 : 12}>
          <Card id="project-settings-business" elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)", scrollMarginTop: 24 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={.6} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                  Datos del negocio
                </Typography>
                {compactMobile ? (
                  <ContextualHelp compact title="Qué se configura acá" text="Esta sección define la identidad base, el perfil del negocio, capacidades opcionales, publicación y mantenimiento técnico. Las operaciones diarias siguen en sus herramientas del panel." />
                ) : null}
              </Stack>
              <Typography variant="body2" sx={{ display: { xs: compactMobile ? "none" : "block", sm: "block" }, mt: .45, mb: 2, color: "rgba(25,36,47,.62)" }}>
                Mantené acá la identidad base del proyecto. Las tareas del día a día siguen en sus secciones propias.
              </Typography>

              <Box component="form" onSubmit={save}>
                <Stack spacing={2}>
                  {!compactMobile || canEditTechnical ? (
                    <>
                      <TextField label="Nombre comercial" value={form.name} onChange={(event) => change("name", event.target.value)} disabled={!canEditTechnical} fullWidth />
                      <TextField select label="Estado operativo" value={form.status} onChange={(event) => change("status", event.target.value)} disabled={!canEditTechnical} fullWidth>
                        <MenuItem value="active">Activo</MenuItem>
                        <MenuItem value="paused">Pausado</MenuItem>
                        <MenuItem value="cancelled">Cancelado</MenuItem>
                      </TextField>
                    </>
                  ) : null}

                  <Divider />

                  <Box id="project-settings-capabilities" sx={{ scrollMarginTop: 24 }}>
                    <Stack direction="row" spacing={.6} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
                        Perfil y capacidades del negocio
                      </Typography>
                      {compactMobile ? (
                        <ContextualHelp compact title="Perfil y capacidades" text="El perfil reordena sugerencias. Las capacidades son decisiones independientes y sólo cambian cuando las activás o desactivás y guardás." />
                      ) : null}
                    </Stack>
                    <Typography variant="body2" sx={{ display: { xs: compactMobile ? "none" : "block", sm: "block" }, mt: 0.45, color: "rgba(25,36,47,.62)" }}>
                      El perfil orienta la configuración, pero no activa ni desactiva funciones de un proyecto existente por sí solo.
                    </Typography>

                    <TextField
                      select
                      label="Tipo de negocio"
                      value={form.businessProfile}
                      onChange={(event) => changeBusinessProfile(event.target.value)}
                      disabled={!canManageBusinessConfiguration}
                      fullWidth
                      sx={{ mt: 2 }}
                      helperText={selectedBusinessProfile?.description || "Podés dejarlo sin definir y activar capacidades manualmente."}
                    >
                      <MenuItem value="">Sin perfil definido</MenuItem>
                      {PROJECT_BUSINESS_PROFILES.map((profileOption) => (
                        <MenuItem key={profileOption.key} value={profileOption.key}>
                          {profileOption.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 2.5,
                        border: "1px solid rgba(105,87,232,.15)",
                        bgcolor: "rgba(105,87,232,.035)",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 950, color: BRAND.dark }}>
                        Qué querés manejar
                      </Typography>
                      <Typography variant="caption" sx={{ display: { xs: compactMobile ? "none" : "block", sm: "block" }, mt: 0.25, color: "rgba(25,36,47,.58)", lineHeight: 1.45 }}>
                        Estas son decisiones guardadas del negocio. Cambiar el tipo sólo reordena las sugerencias: no borra lo que ya elegiste.
                      </Typography>

                      {visibleBusinessGoals.length ? (
                        <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} sx={{ mt: 1.1 }}>
                          {visibleBusinessGoals.map((goal) => {
                            const selected = selectedBusinessGoalKeys.has(goal.key);
                            const suggested = suggestedBusinessGoalKeys.has(goal.key);
                            return (
                              <Chip
                                key={goal.key}
                                label={`${goal.label}${!suggested && selected ? " · elegida" : ""}`}
                                clickable={canManageBusinessConfiguration}
                                onClick={canManageBusinessConfiguration ? () => toggleBusinessGoal(goal.key) : undefined}
                                variant={selected ? "filled" : "outlined"}
                                sx={{
                                  fontWeight: 850,
                                  bgcolor: selected ? "rgba(105,87,232,.12)" : "#fff",
                                  color: selected ? "#6957E8" : BRAND.dark,
                                  borderColor: selected ? "rgba(105,87,232,.38)" : "rgba(25,36,47,.14)",
                                }}
                              />
                            );
                          })}
                        </Stack>
                      ) : (
                        <Typography variant="caption" sx={{ display: "block", mt: 0.9, color: "rgba(25,36,47,.52)" }}>
                          Podés dejar los objetivos sin definir. Al elegir un tipo de negocio te mostramos sugerencias sin activar nada automáticamente.
                        </Typography>
                      )}

                      {explicitBusinessGoalsOutsideProfile.length ? (
                        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: "rgba(25,36,47,.58)" }}>
                          {explicitBusinessGoalsOutsideProfile.length === 1
                            ? "Hay una necesidad elegida por vos que no depende de las sugerencias del perfil y se conserva igualmente."
                            : `Hay ${explicitBusinessGoalsOutsideProfile.length} necesidades elegidas por vos que no dependen de las sugerencias del perfil y se conservan igualmente.`}
                        </Typography>
                      ) : null}
                    </Box>

                    {canManageBusinessConfiguration && selectedSuggestedCapabilities.size > 0 ? (
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                        sx={{ mt: 1.2 }}
                      >
                        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
                          Las sugerencias se agregan sin apagar capacidades que ya uses.
                        </Typography>
                        <Button
                          type="button"
                          size="small"
                          variant="text"
                          onClick={applySelectedProfileSuggestions}
                          sx={{
                            alignSelf: { xs: "flex-start", sm: "center" },
                            minWidth: 0,
                            px: 1,
                            textTransform: "none",
                            fontWeight: 900,
                            color: BRAND.orange,
                          }}
                        >
                          Activar sugeridas
                        </Button>
                      </Stack>
                    ) : null}

                    <Grid container spacing={1.25} sx={{ mt: 0.75 }}>
                      {PROJECT_CAPABILITIES.map((capability) => {
                        const enabled = form.capabilities?.[capability.key] === true;
                        const suggested = selectedSuggestedCapabilities.has(capability.key);
                        return (
                          <Grid item xs={12} sm={6} key={capability.key}>
                            <Paper
                              variant="outlined"
                              sx={{
                                px: 1.5,
                                py: 1.1,
                                borderRadius: 2.5,
                                borderColor: enabled ? "rgba(243,90,58,.32)" : "rgba(25,36,47,.09)",
                                bgcolor: enabled ? "rgba(243,90,58,.035)" : "#fff",
                              }}
                            >
                              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 900, color: BRAND.dark }}>
                                    {capability.label}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: "block", mt: 0.15, color: "rgba(25,36,47,.58)" }}>
                                    {capability.description}
                                  </Typography>
                                  {suggested ? (
                                    <Typography variant="caption" sx={{ display: "block", mt: 0.2, color: BRAND.orange, fontWeight: 850 }}>
                                      Sugerida para este perfil
                                    </Typography>
                                  ) : null}
                                </Box>
                                <Switch
                                  size="small"
                                  checked={enabled}
                                  disabled={!canManageBusinessConfiguration}
                                  onChange={(event) => changeCapability(capability.key, event.target.checked)}
                                  inputProps={{ "aria-label": `Habilitar ${capability.label}` }}
                                />
                              </Stack>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>

                    {canManageBusinessConfiguration ? (
                      <Button
                        type="button"
                        disabled={saving}
                        startIcon={<SaveRoundedIcon />}
                        onClick={saveBusinessConfiguration}
                        variant="outlined"
                        sx={{ mt: 1.75, borderRadius: 999, textTransform: "none", fontWeight: 900, color: BRAND.orange, borderColor: "rgba(243,90,58,.35)" }}
                      >
                        {saving ? "Guardando..." : "Guardar perfil y capacidades"}
                      </Button>
                    ) : null}
                  </Box>

                  <Alert
                    severity="info"
                    action={
                      <Button
                        type="button"
                        color="inherit"
                        size="small"
                        onClick={() => navigate(subscriptionPath)}
                        sx={{ textTransform: "none", fontWeight: 900 }}
                      >
                        Administrar plan
                      </Button>
                    }
                  >
                    {compactMobile ? "Revisá tu plan y medio de pago." : "El plan, la mensualidad y el medio de pago se administran juntos desde Plan y suscripción."}
                  </Alert>

                  <Box id="project-settings-domain" sx={{ scrollMarginTop: 24 }}>
                    <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
                      Dominio y publicación
                    </Typography>
                    <Typography variant="body2" sx={{ display: { xs: compactMobile ? "none" : "block", sm: "block" }, mt: .45, mb: 1.5, color: "rgba(25,36,47,.62)" }}>
                      Revisá la publicación y conectá un dominio propio solamente si lo necesitás.
                    </Typography>
                    {project ? (
                      <ProjectDeploymentCard
                        project={project}
                        compact
                        canRetry={canManageMercadoPago}
                        canManageDomain={canManageBusinessConfiguration}
                        canManageInfrastructure={canEditTechnical}
                        onProjectChange={handleDeploymentProjectChange}
                      />
                    ) : null}
                  </Box>

                  <Divider />

                  <Box id="project-settings-advanced" sx={{ scrollMarginTop: 24 }}>
                    <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
                      Configuración avanzada
                    </Typography>
                    <Typography variant="body2" sx={{ display: { xs: compactMobile ? "none" : "block", sm: "block" }, mt: .45, mb: 1.4, color: "rgba(25,36,47,.62)" }}>
                      Usá estas opciones para mantenimiento puntual. No hace falta revisarlas para operar el negocio todos los días.
                    </Typography>

                    {!compactMobile && project ? <ProjectDeploymentHistory project={project} /> : null}

                    <Alert
                      severity="info"
                      sx={{ mt: !compactMobile && project ? 1.5 : 0 }}
                      action={
                        form.modules.landing ? (
                          <Button
                            type="button"
                            color="inherit"
                            size="small"
                            onClick={() => navigate(`/dashboard/proyectos/${projectId}/pagina`)}
                            sx={{ textTransform: "none", fontWeight: 900 }}
                          >
                            Editar identidad
                          </Button>
                        ) : null
                      }
                    >
                      {compactMobile ? "Editá la identidad visual desde Mi web." : <>Logos, favicon, colores y tipografías se administran desde Landing &gt; Ajustes. Esa configuración es la fuente pública del sitio.</>}
                    </Alert>

                    {canEditTechnical && (
                      <Button type="submit" disabled={saving} startIcon={<SaveRoundedIcon />} variant="contained" sx={{ mt: 1.5, alignSelf: "flex-start", bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
                        {saving ? "Guardando..." : "Guardar configuración técnica"}
                      </Button>
                    )}
                  </Box>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        )}

        {showAccess && (
          <Grid item xs={12} lg={showGeneral ? 5 : 12}>
            {canManageProjectAccess ? (
              <ProjectAccessManager projectId={projectId} currentMember={member} />
            ) : (
              <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Equipo y accesos</Typography>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Solo el owner o un administrador autorizado puede modificar roles, cajas y permisos.
                  </Alert>
                </CardContent>
              </Card>
            )}
          </Grid>
        )}
      </Grid>
      )}
    </Stack>
  );
}
